import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HelpCenterIcon from '@mui/icons-material/HelpCenter';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ShieldIcon from '@mui/icons-material/Shield';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { getHelpForRoute } from '../data/helpContent';
import { getPermissionsForRoute } from '../lib/pagePermissions';
import { usePermissionContext } from '../context/PermissionContext';
import { usePermissionAudit, type PermissionAuditUser } from '../api/hooks/usePermissions';

const DRAWER_WIDTH = 380;

interface HelpDrawerProps {
  open: boolean;
  onClose: () => void;
}

export function HelpDrawer({ open, onClose }: HelpDrawerProps) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const page = getHelpForRoute(pathname);
  const { isSuperAdmin } = usePermissionContext();
  const pagePermissions = getPermissionsForRoute(pathname);
  const auditEnabled = open && isSuperAdmin && pagePermissions.length > 0;
  const { data: auditData, isLoading: auditLoading } = usePermissionAudit(pagePermissions, auditEnabled);

  const auditMap = auditData?.data ?? {};

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          maxWidth: '100vw',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HelpOutlineIcon color="primary" />
          <Typography variant="h6" fontWeight={700}>
            {t('help.title')}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider />

      <Box sx={{ p: 2, overflow: 'auto', flex: 1, minHeight: 0 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          {page.title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {page.description}
        </Typography>

        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
          {t('help.actions')}
        </Typography>
        <List dense disablePadding sx={{ mb: 3 }}>
          {page.actions.map((action, i) => (
            <ListItem key={i} disableGutters sx={{ py: 0.25 }}>
              <ListItemIcon sx={{ minWidth: 28 }}>
                <CheckCircleOutlineIcon fontSize="small" color="success" />
              </ListItemIcon>
              <ListItemText primary={action} primaryTypographyProps={{ variant: 'body2' }} />
            </ListItem>
          ))}
        </List>

        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
          {t('help.faq')}
        </Typography>
        {page.faq.map((item, i) => (
          <Accordion
            key={i}
            disableGutters
            elevation={0}
            sx={{
              '&:before': { display: 'none' },
              border: '1px solid',
              borderColor: 'divider',
              mb: 1,
              borderRadius: 1,
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.5 } }}
            >
              <Typography variant="body2" fontWeight={500}>{item.q}</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <Typography variant="body2" color="text.secondary">{item.a}</Typography>
            </AccordionDetails>
          </Accordion>
        ))}

        {auditEnabled && (
          <Box sx={{ mt: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <ShieldIcon fontSize="small" color="warning" />
              <Typography variant="subtitle2" fontWeight={700}>
                {t('help.audit.title')}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              {t('help.audit.subtitle')}
            </Typography>

            {auditLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={20} />
              </Box>
            )}

            {!auditLoading && pagePermissions.map((perm) => {
              const users = auditMap[perm] ?? [];
              return (
                <Accordion
                  key={perm}
                  disableGutters
                  elevation={0}
                  sx={{
                    '&:before': { display: 'none' },
                    border: '1px solid',
                    borderColor: 'divider',
                    mb: 1,
                    borderRadius: 1,
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.5, alignItems: 'center', gap: 1 } }}
                  >
                    <Typography variant="body2" fontWeight={600} sx={{ flex: 1, fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      {perm}
                    </Typography>
                    <Chip
                      label={users.length}
                      size="small"
                      color={users.length > 0 ? 'primary' : 'default'}
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0, px: 1.5 }}>
                    {users.length === 0 ? (
                      <Typography variant="caption" color="text.secondary">
                        {t('help.audit.noUsers')}
                      </Typography>
                    ) : (
                      <List dense disablePadding>
                        {users.map((u: PermissionAuditUser) => (
                          <ListItem key={u.email} disableGutters sx={{ py: 0.5, alignItems: 'flex-start' }}>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Typography variant="body2" fontWeight={500}>
                                    {u.name || u.email}
                                  </Typography>
                                  {u.isSuperAdmin && (
                                    <Chip
                                      label={t('help.audit.superAdminBadge')}
                                      size="small"
                                      color="warning"
                                      sx={{ height: 16, fontSize: '0.6rem' }}
                                    />
                                  )}
                                </Box>
                              }
                              secondary={
                                <>
                                  {u.name && (
                                    <Typography variant="caption" color="text.secondary" component="span" sx={{ display: 'block' }}>
                                      {u.email}
                                    </Typography>
                                  )}
                                  {u.viaGroups.length > 0 && (
                                    <Stack
                                      direction="row"
                                      component="span"
                                      spacing={0.5}
                                      sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5, display: 'flex' }}
                                    >
                                      {u.viaGroups.map((g) => (
                                        <Chip
                                          key={g}
                                          label={g}
                                          size="small"
                                          variant="outlined"
                                          component="span"
                                          sx={{ height: 18, fontSize: '0.65rem' }}
                                        />
                                      ))}
                                    </Stack>
                                  )}
                                </>
                              }
                              secondaryTypographyProps={{ component: 'div' }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>
        )}
      </Box>

      <Divider sx={{ flexShrink: 0 }} />
      <Box sx={{ p: 2, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {pathname.startsWith('/calendar') && (
          <Button
            fullWidth
            variant="outlined"
            startIcon={<PlayArrowIcon />}
            onClick={() => {
              onClose();
              window.dispatchEvent(new CustomEvent('pm:start-calendar-tour'));
            }}
            sx={{ py: 1.2, fontWeight: 600, fontSize: '0.9rem' }}
          >
            {t('help.startTour', 'Tour starten')}
          </Button>
        )}
        <Button
          fullWidth
          variant="outlined"
          startIcon={<HelpCenterIcon />}
          onClick={() => { onClose(); navigate('/help'); }}
          sx={{ py: 1.2, fontWeight: 600, fontSize: '0.9rem' }}
        >
          {t('help.helpCenter')}
        </Button>
      </Box>
    </Drawer>
  );
}
